/**
 * usePatientList Hook
 *
 * Business logic for patient list view including:
 * - Fetching patients with pagination
 * - Search functionality with debouncing
 * - Event tracking
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAccessToken } from '@features/user';
import { useEventTracking } from '@services/statsig';
import debounce from 'lodash/debounce';

import APIService from '../../../services/APIService';
import type { Patient } from '../../../types/types';
import type { PatientsResponse } from '../types';

export interface UsePatientListOptions {
  initialPageSize?: number;
}

export interface UsePatientListReturn {
  patients: Patient[];
  isLoading: boolean;
  error: string | null;
  searchTerm: string;
  page: number;
  pageSize: number;
  totalRows: number;
  totalPages: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  isEmpty: boolean;
  isSearchActive: boolean;
  setSearchTerm: (term: string) => void;
  setPage: (page: number) => void;
  setPageSize: (size: number) => void;
  clearSearch: () => void;
  refresh: () => void;
  navigateToPatient: (patientId: string) => void;
  handlePatientCreated: (patientId: string) => void;
}

export function usePatientList(options: UsePatientListOptions = {}): UsePatientListReturn {
  const { initialPageSize = 20 } = options;

  const { getAccessToken } = useAccessToken();
  const navigate = useNavigate();
  const { patient: patientMetrics, search: searchMetrics } = useEventTracking();

  // State
  const [patients, setPatients] = useState<Patient[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTermState] = useState('');
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(initialPageSize);
  const [totalRows, setTotalRows] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  // Refs for tracking
  const abortControllerRef = useRef<AbortController | null>(null);
  const isInitialFetch = useRef(true);

  const fetchPatients = useCallback(
    async (search: string = '', pageNum: number, pageSizeNum: number) => {
      // Cancel previous request
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      abortControllerRef.current = new AbortController();

      setIsLoading(true);
      setError(null);

      try {
        const endpoint = search
          ? `/patient/search?q=${encodeURIComponent(search)}&page=${pageNum + 1}&per_page=${pageSizeNum}`
          : `/patient/list?page=${pageNum + 1}&per_page=${pageSizeNum}`;

        const response = await APIService.makeAPIGetRequest({
          requestString: endpoint,
          accessToken: await getAccessToken(),
        });

        if (response.ok) {
          const data = (await response.value) as PatientsResponse & { total_pages?: number };
          setPatients(data.patients);
          setTotalRows(data.total);
          // API may return totalPages or total_pages, calculate as fallback
          const pages = data.totalPages || data.total_pages || Math.ceil(data.total / pageSizeNum);
          setTotalPages(pages);

          // Track search results or list view
          if (search) {
            searchMetrics.performed({
              search_type: 'patient',
              search_context: 'list_page',
              query_length: search.length,
              results_count: data.total,
              has_results: data.total > 0,
            });
          } else if (!isInitialFetch.current) {
            patientMetrics.listViewed({
              total_count: data.total,
              filtered: false,
              page: pageNum + 1,
              page_size: pageSizeNum,
            });
          }

          isInitialFetch.current = false;
        } else {
          throw new Error('Failed to fetch patients');
        }
      } catch (err) {
        if ((err as Error).name !== 'AbortError') {
          console.error('Error fetching patients:', err);
          setError('Something went wrong while loading patients. Please try again.');
          setPatients([]);
          setTotalRows(0);
          setTotalPages(0);
        }
      } finally {
        setIsLoading(false);
      }
    },
    [getAccessToken, patientMetrics, searchMetrics],
  );

  // Debounced search
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedFetch = useCallback(
    debounce((search: string, pageNum: number, pageSizeNum: number) => {
      void fetchPatients(search, pageNum, pageSizeNum);
    }, 300),
    [fetchPatients],
  );

  // Effect to fetch on changes
  useEffect(() => {
    if (searchTerm) {
      debouncedFetch(searchTerm, page, pageSize);
    } else {
      void fetchPatients(searchTerm, page, pageSize);
    }

    return () => {
      debouncedFetch.cancel();
    };
  }, [searchTerm, page, pageSize, fetchPatients, debouncedFetch]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  const setSearchTerm = useCallback((term: string) => {
    setSearchTermState(term);
    setPage(0); // Reset to first page on search
  }, []);

  const clearSearch = useCallback(() => {
    if (searchTerm) {
      searchMetrics.cleared({
        search_type: 'patient',
        search_context: 'list_page',
        had_results: patients.length > 0,
        query_length: searchTerm.length,
      });
    }
    setSearchTermState('');
    setPage(0);
  }, [searchTerm, patients.length, searchMetrics]);

  const refresh = useCallback(() => {
    void fetchPatients(searchTerm, page, pageSize);
  }, [fetchPatients, searchTerm, page, pageSize]);

  const navigateToPatient = useCallback(
    (patientId: string) => {
      patientMetrics.selected(patientId, {
        source: 'list',
        context: 'viewing',
      });
      navigate(`/patient/${patientId}`);
    },
    [navigate, patientMetrics],
  );

  const handlePatientCreated = useCallback(
    (_patientId: string) => {
      refresh();
    },
    [refresh],
  );

  return {
    patients,
    isLoading,
    error,
    searchTerm,
    page,
    pageSize,
    totalRows,
    totalPages,
    hasNextPage: page < totalPages - 1,
    hasPreviousPage: page > 0,
    isEmpty: !isLoading && patients.length === 0,
    isSearchActive: searchTerm.length > 0,
    setSearchTerm,
    setPage,
    setPageSize,
    clearSearch,
    refresh,
    navigateToPatient,
    handlePatientCreated,
  };
}
